import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

// Fixed round-number equivalents (NOT live FX). Decided by product:
//  * 1 USD  == 1 EUR
//  * 1 USD  == 1 GBP
//  * 1 USD  == 100 INR
//  * 1 USD  == 8   DKK
//  * All amounts are then snapped to the nearest 10 units of the target currency.
export const CURRENCIES = [
  { code: 'USD', symbol: '$',  multiplier: 1,   locale: 'en-US', prefix: true,  space: false },
  { code: 'EUR', symbol: '€',  multiplier: 1,   locale: 'de-DE', prefix: true,  space: false },
  { code: 'GBP', symbol: '£',  multiplier: 1,   locale: 'en-GB', prefix: true,  space: false },
  { code: 'INR', symbol: '₹',  multiplier: 100, locale: 'en-IN', prefix: true,  space: false },
  { code: 'DKK', symbol: 'kr', multiplier: 8,   locale: 'da-DK', prefix: false, space: true  },
];

// Browser-locale heuristic for the initial pick.
//  * en-US / en-CA / es-MX etc → USD
//  * en-GB → GBP
//  * en-IN / hi-IN → INR
//  * da-DK → DKK
//  * any other EU locale (de, fr, es, it, nl, pt, pl, sv, fi, cs, hu, ro, el, bg, hr, sk, sl, lt, lv, et, ga, mt) → EUR
//  * anything else → USD
const EU_LANGS = new Set([
  'de', 'fr', 'es', 'it', 'nl', 'pt', 'pl', 'sv', 'fi', 'cs',
  'hu', 'ro', 'el', 'bg', 'hr', 'sk', 'sl', 'lt', 'lv', 'et', 'ga', 'mt',
]);
const NORTH_AMERICAN_REGIONS = new Set(['US', 'CA', 'MX', 'PR']);

const detectCurrencyFromLocale = () => {
  try {
    if (typeof navigator === 'undefined') return 'USD';
    const raw = (navigator.language || 'en-US').trim();
    const [lang, region] = raw.split('-');
    const regionUpper = (region || '').toUpperCase();

    if (regionUpper === 'IN') return 'INR';
    if (regionUpper === 'DK') return 'DKK';
    if (regionUpper === 'GB') return 'GBP';
    if (NORTH_AMERICAN_REGIONS.has(regionUpper)) return 'USD';
    if (regionUpper && EU_LANGS.has(lang.toLowerCase())) return 'EUR';
    // No country code → fall back on language only
    if (!regionUpper && EU_LANGS.has(lang.toLowerCase())) return 'EUR';
    return 'USD';
  } catch (e) {
    return 'USD';
  }
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [code, setCodeState] = useState(() => {
    try {
      const saved = typeof window !== 'undefined' && window.localStorage.getItem('itanta.currency');
      if (saved && CURRENCIES.some((c) => c.code === saved)) return saved;
    } catch (e) { /* ignore */ }
    return detectCurrencyFromLocale();
  });

  const setCode = (next) => {
    setCodeState(next);
    try { window.localStorage.setItem('itanta.currency', next); } catch (e) { /* ignore */ }
  };

  // When the signed-in user has an assigned region, lock the display currency to
  // the region's preferred currency. This overrides the browser-locale guess and
  // any previously-saved pref so admins can govern what each user sees.
  useEffect(() => {
    if (user?.preferred_currency && CURRENCIES.some((c) => c.code === user.preferred_currency)) {
      setCodeState(user.preferred_currency);
    }
  }, [user?.preferred_currency]);

  const value = useMemo(() => {
    const currency = CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];

    // Convert USD → active currency, snapped to the nearest 10 units.
    const convert = (usd) => {
      if (typeof usd !== 'number' || Number.isNaN(usd)) return 0;
      const raw = usd * currency.multiplier;
      return Math.round(raw / 10) * 10;
    };

    const formatMoney = (usd, opts = {}) => {
      if (usd === null || usd === undefined || Number.isNaN(usd)) return '';
      const converted = convert(usd);
      const decimals = opts.decimals ?? 0;
      const numStr = converted.toLocaleString(currency.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      if (currency.prefix) return `${currency.symbol}${numStr}`;
      return `${numStr}${currency.space ? ' ' : ''}${currency.symbol}`;
    };

    return {
      code,
      setCode,
      currency,
      currencies: CURRENCIES,
      formatMoney,
      convert,
    };
  }, [code]);

  return (
    <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};
